import {
  Alert,
  Checkbox,
  Heading,
  Link,
  Spinner,
  Switch,
  TextLink,
} from '@mm/components';
import { ENTER_CODE } from '@mm/constants/keys-codes';
import {
  CheckedLightIcon,
  EditIcon,
  PlusCircleIcon,
  TrashIcon,
} from '@mm/icons';
import { useTwilioCRUDActions } from '@mm/state/actions/TwilioCRUDActions';
import {
  twillioCallForwardingNumberSelector,
  twillioNumberSelector,
  twillioTestNumbersSelector,
} from '@mm/state/selectors/twillio';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import { colors } from '@mm/utils';
import classNames from 'classnames';
import { parsePhoneNumberFromString, PhoneNumber } from 'libphonenumber-js';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { debounce, map } from 'lodash';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { PrimaryToolTip } from '../../../../../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import './TestPhoneNumberForm.pcss';
import { CheckIcon } from '../../../../../../../../common/component-icons/CheckIcon/CheckIcon';
import { DeleteTwoTone } from '@ant-design/icons';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';

type InputProps = {
  className: string;
  value: string;
  onChange(value: string, parsed?: PhoneNumber): void;
  placeholder?: string;
};

type TestPhoneNumber = {
  number: string;
  isActive: boolean;
  name?: string;
};

const PhoneInput = ({
  className,
  value,
  onChange = () => {},
  placeholder,
  ...rest
}: InputProps) => {
  const onChangeCallback = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value = '' } = e.target;
      onChange(value, parsePhoneNumberFromString(value));
    },
    [],
  );
  return (
    <input
      className={classNames(className)}
      type="tel"
      autoComplete="tel"
      value={value}
      onChange={onChangeCallback}
      placeholder={placeholder}
      {...rest}
    />
  );
};

type Props = {
  className?: string;
  current?: boolean;
  edit?: boolean;
  all?: boolean;
  test?: boolean;
  callForwarding?: boolean;
  smsCount?: number;
  campaignId?: number;
  schedulerId?: number;
  schedulerIndex?: number;
  activeTestNumbers?: string[];
  onSendTestSms?: () => void;
  showSelectedLabel?: boolean;
  facebookPage?: EnhancedBillingPage;
};

// tslint:disable-next-line:cyclomatic-complexity
export const TestPhoneNumberForm = ({
  className,
  current = true,
  edit = true,
  all = true,
  test = false,
  callForwarding = true,
  smsCount,
  campaignId,
  schedulerId,
  schedulerIndex = 0,
  activeTestNumbers,
  onSendTestSms,
  showSelectedLabel,
  facebookPage,
}: Props) => {
  const twilioNumber = useSelector(twillioNumberSelector);
  const twilioTestNumbers = useSelector(twillioTestNumbersSelector) || [];
  const callForwardingNumber = useSelector(twillioCallForwardingNumberSelector);
  const [twilioTestNumbersArray, setTwilioTestNumbersArray] = useState<
    TestPhoneNumber[]
  >([]);
  const [callsForwardingNumber, setCallsForwardingNumber] = useState<string>(
    callForwardingNumber || '',
  );
  const [callsForwardingSwitchActive, setCallsForwardingSwitchActive] =
    useState(!!callsForwardingNumber.length);
  const activeTwilioTestNumbers = twilioTestNumbersArray.filter(
    number => number.isActive,
  );

  useEffect(() => {
    const twilioTestNumbersNew: TestPhoneNumber[] = twilioTestNumbers.map(
      twilioTestNumber => {
        const [number, name] = twilioTestNumber.split('#');
        const isActive = !!activeTestNumbers?.includes(number);
        return { name, number, isActive };
      },
    );
    setTwilioTestNumbersArray(twilioTestNumbersNew);
  }, [twilioTestNumbers]);

  const testNumbersFull = twilioTestNumbers.length === 5;

  const activeFacebookPageId = useSelector(activeFacebookPageIdSelector);
  const facebookPageId = facebookPage ? facebookPage.id : activeFacebookPageId;
  const activeChatbotId = useSelector(activeChatbotIdSelector);

  const { updateTestNumbers, updateCallsForwarding } = useTwilioCRUDActions();
  const { testScheduler, retrieve, retrieveAll } = useCampaignCRUDActions();

  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  const [nick, setNick] = useState('');
  const [isInputsVisible, setIsInputsVisible] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSendingFailed, setIsSendingFailed] = useState(false);
  const [isShowSuccessMessage, setIsShowSuccessMessage] = useState(false);

  const parsed = parsePhoneNumberFromString(testPhoneNumber);
  const isValid =
    parsed?.isValid() &&
    ['US', 'CA'].includes(parsed?.country as string) &&
    !map(twilioTestNumbersArray, 'number').includes(parsed?.number as string);

  const onTestNumberRemove = async (number: string, name?: string) => {
    const testNumber = name ? `${number}#${name}` : number;
    await updateTestNumbers(facebookPageId, {
      testNumbers: twilioTestNumbers.filter(num => num !== testNumber),
    });
  };

  const updateTestNumber = async () => {
    if (isValid) {
      const testNumber = nick ? `${testPhoneNumber}#${nick}` : testPhoneNumber;
      await updateTestNumbers(facebookPageId, {
        testNumbers: [...twilioTestNumbers, testNumber.trim()],
      });
      setTestPhoneNumber('');
      setNick('');
      setIsInputsVisible(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.keyCode === ENTER_CODE) {
      updateTestNumber();
    }
  };

  const onTestNumberChange = (number: string) => setTestPhoneNumber(number);

  const onSetNumberActive = (testPhone: TestPhoneNumber) => {
    const testNumbers = twilioTestNumbersArray.map(number => {
      if (number.number === testPhone.number) {
        return {
          ...testPhone,
          isActive: !testPhone.isActive,
        };
      }
      return number;
    });
    setTwilioTestNumbersArray(testNumbers);
  };

  const onSending = async (campaignId: number, schedulerId: number) => {
    const testNumbers = activeTwilioTestNumbers.map(number => number.number);
    try {
      const testSchedulerResponse = await testScheduler(
        campaignId,
        schedulerId,
        testNumbers,
      );
      if (testSchedulerResponse.success) {
        setIsShowSuccessMessage(true);
        setTimeout(() => {
          setIsShowSuccessMessage(false);
        }, 60000);
      }
    } catch (e) {
      setIsSending(false);
      setIsSendingFailed(true);
      setTimeout(() => {
        setIsSendingFailed(false);
      }, 30000);
    }
  };

  const onClickSendTestSms = debounce(async () => {
    if (campaignId) {
      setIsSendingFailed(false);
      setIsSending(true);
      const response = await onSendTestSms?.();
      const campaign = response ? response : await retrieve(campaignId);
      const schedulerIdLocal = schedulerId
        ? schedulerId
        : campaign?.schedulersAttributes?.[schedulerIndex]?.id;
      if (schedulerIdLocal) {
        await onSending(campaignId, schedulerIdLocal);
        if (campaign) {
          await retrieveAll({
            botId: activeChatbotId,
            pageNumber: 1,
            pageSize: 25,
            dripCampaign: campaign.scheduleType === 'drip',
            withSchedulersAttributes: true,
          });
        }
      }
      setIsSending(false);
    }
  }, 500);

  const parsedCallsForwardingNumber = parsePhoneNumberFromString(
    callsForwardingNumber,
  );
  const isValidCallsForwardingNumber =
    parsedCallsForwardingNumber?.isValid() &&
    ['US', 'CA'].includes(parsedCallsForwardingNumber?.country as string);

  const updateCallsForwardingNumber = async () => {
    if (isValidCallsForwardingNumber) {
      await updateCallsForwarding(facebookPageId, {
        callForwardingNumber: callsForwardingNumber.trim(),
      });
      setCallsForwardingSwitchActive(true);
    }
  };

  const onCallsForwardingToggleChange = async () => {
    if (callsForwardingNumber) {
      await updateCallsForwarding(facebookPageId, {
        callForwardingNumber: '',
      });
      setCallsForwardingNumber('');
    }

    setCallsForwardingSwitchActive(!callsForwardingSwitchActive);
  };

  const saveBtn = (
    <PrimaryButton
      className="phone-number__save-btn"
      onClick={updateTestNumber}
      disabled={!isValid || testNumbersFull}
    >
      {test ? 'Add' : 'Save'}
    </PrimaryButton>
  );

  const cancelBtn = (
    <PrimaryButton
      color={ButtonColor.secondaryBlue}
      className="phone-number__cancel-btn"
      onClick={() => {
        setTestPhoneNumber('');
        setNick('');
        setIsInputsVisible(false);
      }}
    >
      Cancel
    </PrimaryButton>
  );

  return (
    <div className={classNames(className, { 'phone-number--test': test })}>
      {current && (
        <div className="phone-number__wrapper">
          <Heading
            size={5}
            isBold={false}
            className="phone-number__phone-text"
            text="Phone Number:"
          />

          <div className="phone-number__box">
            <span className="phone-number__text">{twilioNumber?.number}</span>
            {showSelectedLabel && (
              <div className="phone-number__text-selected">
                <CheckIcon color="#5B8C00" /> Selected
              </div>
            )}
          </div>
        </div>
      )}

      <div className="phone-number__test-numbers">
        {edit && (
          <div className="phone-number__wrapper" onKeyDown={onKeyDown}>
            {test && (
              <Heading
                className="phone-number__phone-text"
                size={5}
                isBold={false}
                text={`Test ${smsCount} SMS`}
              />
            )}
            <Heading
              className="phone-number__phone-text"
              size={5}
              isBold={!callForwarding}
              text={test ? 'Send on:' : 'Testing number:'}
            />

            {all && (
              <div className="phone-number__test-numbers-wrapper">
                {twilioTestNumbersArray?.map(testNumber => (
                  <div
                    key={testNumber.number}
                    className="phone-number__test-number-container"
                  >
                    <div className="phone-number__test-number-checkbox-container">
                      {test && (
                        <Checkbox
                          isChecked={testNumber.isActive}
                          onToggle={() => onSetNumberActive(testNumber)}
                          className="phone-number__test-number-checkbox"
                        />
                      )}
                      <div className="phone-number__test-number-checkbox-label">
                        {testNumber.name}{' '}
                        <span
                          className={classNames({
                            'phone-number__test-number-checkbox-number':
                              !!testNumber.name,
                          })}
                        >
                          {testNumber.number}
                        </span>
                      </div>
                    </div>
                    <div className="phone-number__test-number-actions">
                      <TrashIcon
                        className="phone-number__test-number-delete-icon"
                        color="#312D4A"
                        onClick={() =>
                          onTestNumberRemove(testNumber.number, testNumber.name)
                        }
                      />
                      <EditIcon
                        className="phone-number__test-number-edit-icon"
                        color="#312D4A"
                        onClick={() => {
                          onTestNumberRemove(
                            testNumber.number,
                            testNumber.name,
                          );
                          setIsInputsVisible(true);
                          setTestPhoneNumber(testNumber.number);
                          setNick(testNumber.name ?? '');
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!testNumbersFull && !isShowSuccessMessage && (
              <div className="phone-number_phone-edit-area">
                <div className="phone-number_phone-input-container">
                  <>
                    {!isInputsVisible ? (
                      <div
                        className="phone-number_add-phone-number"
                        onClick={() => setIsInputsVisible(true)}
                      >
                        <PlusCircleIcon color={colors.accentBlue} />
                        Add your phone number
                      </div>
                    ) : (
                      <div className="phone-number_inputs-container">
                        <PhoneInput
                          placeholder="+1"
                          value={testPhoneNumber}
                          onChange={onTestNumberChange}
                          className={classNames('phone-number__phone-input', {
                            'phone-number__input--valid': isValid,
                          })}
                        />
                        <input
                          placeholder="Nick (optional)"
                          value={nick}
                          onChange={e => setNick(e.target.value)}
                          className={classNames('phone-number__nick-input', {
                            'phone-number__input--valid': !!nick?.length,
                          })}
                        />
                      </div>
                    )}
                  </>
                </div>

                {testNumbersFull ? (
                  <PrimaryToolTip
                    anchorElement={saveBtn}
                    useHover
                    position="top"
                    classNameArrow="phone-number__test-tooltip-arrow"
                  >
                    Max test number capacity reached
                  </PrimaryToolTip>
                ) : isInputsVisible ? (
                  <>
                    {saveBtn}
                    {cancelBtn}
                  </>
                ) : (
                  <></>
                )}
              </div>
            )}
            {isShowSuccessMessage && (
              <>
                <div className="phone-number__success-message-title">
                  <CheckedLightIcon size={20} /> SMS has been sent
                </div>
                <div className="phone-number__success-message">
                  It can take up to a 1 min. to get <br /> Didn’t receive or
                  have issue?{' '}
                  <Link href="mailto:support@mobilemonkey.com" isBlank>
                    Let us know
                  </Link>
                </div>
              </>
            )}
            {test && (
              <div className="phone-number__send-btn-container">
                <PrimaryButton
                  className={classNames('phone-number__send-btn', {
                    'phone-number__send-btn--sending': isSending,
                  })}
                  onClick={onClickSendTestSms}
                  disabled={!activeTwilioTestNumbers.length || !smsCount}
                  leftIcon={
                    <Spinner
                      show={isSending}
                      color="#FFFFFF"
                      width={16}
                      size={16}
                      position="relative"
                      backgroundColor="transparent"
                    />
                  }
                >
                  {isSending ? 'Sending' : 'Send Test SMS'}
                </PrimaryButton>
                {isSendingFailed && (
                  <div className="phone-number__send-status--failed">
                    Failed sending
                  </div>
                )}
              </div>
            )}

            <PrimaryToolTip
              anchorElement={
                <Alert type="info" showCloseIcon={false}>
                  US & Canada numbers allowed only
                </Alert>
              }
              position="top"
              classNameArrow="phone-number__test-tooltip-arrow"
            >
              We won’t deduct send credits to for messages sent to this test
              number
            </PrimaryToolTip>
          </div>
        )}

        {testNumbersFull && (
          <TextLink className="phone-number__test-number-warning">
            Max test number capacity reached
          </TextLink>
        )}
      </div>
      {callForwarding && (
        <div className="phone-number__wrapper">
          <div className="phone-number__calls-forwarding-header-wrapper">
            <Switch
              css={{
                container: 'phone-number__calls-forwarding-switch',
                thumb: classNames(
                  'phone-number__calls-forwarding-switch-thumb',
                  callsForwardingSwitchActive && 'active',
                ),
              }}
              checked={callsForwardingSwitchActive}
              hideLabel
              onChange={onCallsForwardingToggleChange}
            />
            <Heading
              size={5}
              isBold={false}
              className="phone-number__calls-forwarding-phone-text"
              text="Call Forwarding"
            />
          </div>

          {callsForwardingSwitchActive && (
            <>
              <div className="phone-number__calls-forwarding-subheader">
                Specify the phone number to forward calls to
              </div>
              <div className="phone-number__calls-forwarding-input-wrapper">
                <PhoneInput
                  placeholder="+1xxxxxxxxxx"
                  value={callsForwardingNumber}
                  onChange={v => setCallsForwardingNumber(v)}
                  className={classNames(
                    'phone-number__phone-input',
                    'phone-number__calls-forwarding-input',
                    {
                      'phone-number__calls-forwarding-input-with-opacity':
                        !callsForwardingNumber?.length,
                    },
                  )}
                />
                <PrimaryButton
                  className="phone-number__call-forwarding-save-btn"
                  onClick={updateCallsForwardingNumber}
                  disabled={
                    !isValidCallsForwardingNumber ||
                    callsForwardingNumber === callForwardingNumber
                  }
                >
                  Save
                </PrimaryButton>
                {callForwardingNumber && (
                  <DeleteTwoTone
                    className="phone-number__delete-btn"
                    onClick={onCallsForwardingToggleChange}
                    twoToneColor={[
                      'var(--color-accent-blue)',
                      'rgba(47,84,235,0.1)',
                    ]}
                  />
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
